import { useState, useEffect, useMemo } from 'react'
import { Ticket } from '../types'
import { useAuth } from '../hooks/useAuth'
import { useDeepLinkById } from '../hooks/useDeepLinkById'
import { useAlert } from '../contexts/AlertContext'
import { loadPropertyMoveTickets } from '../lib/apiPhpAdapter'
import CreatePropertyMoveTicketForm from '../components/CreatePropertyMoveTicketForm'
import PropertyMoveTicketList from '../components/PropertyMoveTicketList'
import Pagination from '../components/Pagination'
import { useIsMobile } from '../hooks/useMediaQuery'
import './PropertyRequesterView.css'

function PropertyRequesterView() {
  const { user, loading: authLoading } = useAuth()
  const { showAlert } = useAlert()
  const [tickets, setTickets] = useState<Ticket[]>([])
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const isMobile = useIsMobile()
  const itemsPerPage = isMobile ? 20 : 100

  useEffect(() => {
    if (user && !authLoading) {
      loadTicketsData()
    } else if (!authLoading && !user) {
      setLoading(false)
    }
  }, [user, authLoading])

  const loadTicketsData = async () => {
    if (!user) return
    
    try {
      setLoading(true)
      const propertyTickets = await loadPropertyMoveTickets()
      const myPropertyTickets = propertyTickets.filter(t => {
        const isRequester = t.requester_id === user.id
        const isAssignedTo = t.assigned_to_id === user.id
        return isRequester || isAssignedTo
      })
      setTickets(myPropertyTickets)
    } catch (error) {
      console.error('Chyba při načítání žádostí o přesun majetku:', error)
      alert('Chyba při načítání žádostí. Zkuste to prosím znovu.')
      setTickets([])
    } finally {
      setLoading(false)
    }
  }

  useDeepLinkById({
    items: tickets,
    ready: !loading,
    onFound: (t) => {
      if (t.status === 'new') setStatusFilter('new')
      else if (
        t.status === 'in_progress' ||
        t.status === 'recipient_needs_transport' ||
        t.status === 'recipient_accepted_self_transport' ||
        t.status === 'awaiting_transport'
      ) {
        setStatusFilter('in_progress')
      } else if (t.status === 'rejected') setStatusFilter('rejected')
      else if (t.status === 'completed') setStatusFilter('completed')
      else setStatusFilter(null)
    },
    onNotFound: () => showAlert('Žádost nenalezena nebo již není ve vaší frontě.', 'error'),
    elementIdFor: (id) => `ticket-${id}`,
  })

  // Простая категоризация по статусам для статистики
  const newTickets = useMemo(() => tickets.filter(t => t.status === 'new'), [tickets])
  const inProgressTickets = useMemo(() => tickets.filter(t => 
    t.status === 'in_progress' || 
    t.status === 'recipient_needs_transport' || 
    t.status === 'recipient_accepted_self_transport' ||
    t.status === 'awaiting_transport'
  ), [tickets])
  const rejectedTickets = useMemo(() => tickets.filter(t => t.status === 'rejected'), [tickets])
  const completedTickets = useMemo(() => tickets.filter(t => t.status === 'completed'), [tickets])

  // Фильтрация заявок по выбранному статусу
  const filteredTickets = useMemo(() => {
    if (!statusFilter) return tickets
    
    if (statusFilter === 'new') return newTickets
    if (statusFilter === 'in_progress') return inProgressTickets
    if (statusFilter === 'rejected') return rejectedTickets
    if (statusFilter === 'completed') return completedTickets
    
    return tickets
  }, [tickets, statusFilter, newTickets, inProgressTickets, rejectedTickets, completedTickets])

  // Пагинация
  const totalPages = Math.ceil(filteredTickets.length / itemsPerPage)
  const paginatedTickets = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    return filteredTickets.slice(startIndex, endIndex)
  }, [filteredTickets, currentPage, itemsPerPage])

  // Сброс страницы при изменении фильтра
  useEffect(() => {
    setCurrentPage(1)
  }, [statusFilter])

  const handleTicketCreated = async () => {
    setShowCreateForm(false)
    // Небольшая задержка для того, чтобы БД успела обновиться
    await new Promise(resolve => setTimeout(resolve, 500))
    await loadTicketsData()
  }

  if (authLoading || loading || !user) {
    return (
      <div className="property-requester-view">
        <div className="loading">Načítání...</div>
      </div>
    )
  }

  if (showCreateForm) {
    return (
      <div className="property-requester-view">
        <CreatePropertyMoveTicketForm
          onCancel={() => setShowCreateForm(false)}
          onSuccess={handleTicketCreated}
        />
      </div>
    )
  }

  return (
    <div className="property-requester-view">
      <div className="property-requester-header">
        <button 
          className="create-ticket-button"
          onClick={() => setShowCreateForm(true)}
        >
          + Vytvořit žádost o přesun majetku
        </button>
      </div>

      {!showCreateForm && !loading && (
        <>
          <div className="admin-stats">
            <div 
              className={`stat-card ${statusFilter === 'new' ? 'active' : ''}`}
              onClick={() => setStatusFilter(statusFilter === 'new' ? null : 'new')}
              style={{ cursor: 'pointer' }}
            >
              <h3>Nové</h3>
              <p className="stat-number">{newTickets.length}</p>
            </div>
            <div 
              className={`stat-card ${statusFilter === 'in_progress' ? 'active' : ''}`}
              onClick={() => setStatusFilter(statusFilter === 'in_progress' ? null : 'in_progress')}
              style={{ cursor: 'pointer' }}
            >
              <h3>V práci</h3>
              <p className="stat-number">{inProgressTickets.length}</p>
            </div>
            <div 
              className={`stat-card ${statusFilter === 'rejected' ? 'active' : ''}`}
              onClick={() => setStatusFilter(statusFilter === 'rejected' ? null : 'rejected')}
              style={{ cursor: 'pointer', backgroundColor: statusFilter === 'rejected' ? '#fee2e2' : undefined, borderColor: statusFilter === 'rejected' ? '#ef4444' : undefined }}
            >
              <h3>Zamítnuto</h3>
              <p className="stat-number">{rejectedTickets.length}</p>
            </div>
            <div 
              className={`stat-card ${statusFilter === 'completed' ? 'active' : ''}`}
              onClick={() => setStatusFilter(statusFilter === 'completed' ? null : 'completed')}
              style={{ cursor: 'pointer' }}
            >
              <h3>Vyřízeno</h3>
              <p className="stat-number">{completedTickets.length}</p>
            </div>
            <div 
              className={`stat-card ${statusFilter === null ? 'active' : ''}`}
              onClick={() => setStatusFilter(null)}
              style={{ cursor: 'pointer', backgroundColor: statusFilter === null ? '#0d9488' : undefined, color: statusFilter === null ? 'white' : undefined }}
            >
              <h3>Celkem</h3>
              <p className="stat-number">{tickets.length}</p>
            </div>
          </div>
        </>
      )}

      {loading ? (
        <div className="loading">Načítání...</div>
      ) : showCreateForm ? null : (
        <>
          {statusFilter ? (
            <div className="tickets-section">
              <h2>
                {statusFilter === 'new' && `Nové žádosti (${newTickets.length})`}
                {statusFilter === 'in_progress' && `V práci (${inProgressTickets.length})`}
                {statusFilter === 'rejected' && `Zamítnuté žádosti (${rejectedTickets.length})`}
                {statusFilter === 'completed' && `Vyřízené žádosti (${completedTickets.length})`}
              </h2>
              {paginatedTickets.length > 0 ? (
                <>
                  <PropertyMoveTicketList tickets={paginatedTickets} currentUserId={user?.id} onTicketUpdate={loadTicketsData} />
                  {totalPages > 1 && (
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={setCurrentPage}
                      itemsPerPage={itemsPerPage}
                      totalItems={filteredTickets.length}
                    />
                  )}
                </>
              ) : (
                <p style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                  {statusFilter === 'new' && 'Žádné nové žádosti'}
                  {statusFilter === 'in_progress' && 'Žádné žádosti v práci'}
                  {statusFilter === 'rejected' && 'Žádné zamítnuté žádosti'}
                  {statusFilter === 'completed' && 'Žádné vyřízené žádosti'}
                </p>
              )}
            </div>
          ) : (
            <>
              {newTickets.length > 0 && (
                <div className="tickets-section">
                  <h2>Nové žádosti ({newTickets.length})</h2>
                  <PropertyMoveTicketList tickets={newTickets} currentUserId={user?.id} onTicketUpdate={loadTicketsData} />
                </div>
              )}
              
              {inProgressTickets.length > 0 && (
                <div className="tickets-section">
                  <h2>V práci ({inProgressTickets.length})</h2>
                  <PropertyMoveTicketList tickets={inProgressTickets} currentUserId={user?.id} onTicketUpdate={loadTicketsData} />
                </div>
              )}
              
              {rejectedTickets.length > 0 && (
                <div className="tickets-section">
                  <h2 style={{ color: '#ef4444' }}>Zamítnuté žádosti ({rejectedTickets.length})</h2>
                  <PropertyMoveTicketList tickets={rejectedTickets} currentUserId={user?.id} onTicketUpdate={loadTicketsData} />
                </div>
              )}
              
              {completedTickets.length > 0 && (
                <div className="tickets-section">
                  <h2>Vyřízené žádosti ({completedTickets.length})</h2>
                  <PropertyMoveTicketList tickets={completedTickets} currentUserId={user?.id} onTicketUpdate={loadTicketsData} />
                </div>
              )}
              
              {tickets.length === 0 && (
                <p style={{ padding: '2rem', textAlign: 'center', color: '#666', fontStyle: 'italic' }}>
                  Žádné žádosti o přesun majetku
                </p>
              )}
            </>
          )}
        </>
      )}
    </div>
  )
}

export default PropertyRequesterView

